<template>
  <section>
    <div class="content-devx no-margin margin-right-half">
      <div class="content-separatos">
        <section class="product-chart">
          <DxChart
            style="width: 100%"
            palette="SoftPastel"
            :data-source="chartDataWithAxis.data"
            @legend-click="onLegendClick"
          >
            <DxSeriesTemplate 
              name-field="productName" 
              :customize-series="customizeSeries"
            />
            <DxTooltip
              :enabled="true"
              :z-index="999999"
              :customize-tooltip="customizeTooltip"
            />
            <DxCommonSeriesSettings
              argument-field="monthWeek"
              value-field="value"
              type="stackedbar"
            />
            <DxValueAxis name="primary" :allowDecimals="false">
              <DxLabel :customize-text="customizeAxisLabel" />
            </DxValueAxis>
            <DxValueAxis 
              v-if="chartDataWithAxis.useSecondAxis"
              name="secondary" 
              position="right"
              :allowDecimals="true"
            >
              <DxLabel :customize-text="customizeSecondaryAxisLabel" />
            </DxValueAxis>
            <DxLegend
              vertical-alignment="top"
              horizontal-alignment="center"
              item-text-position="right"
            />
            <DxSize :height="210" />
          </DxChart>
        </section>
      </div>
    </div>
  </section>
</template>

<script>
import {
  DxChart,
  DxSeries,
  DxCommonSeriesSettings,
  DxLegend,
  DxSeriesTemplate,
  DxTooltip,
  DxSize,
  DxValueAxis,
  DxLabel,
} from "devextreme-vue/chart";
import { DxDataGrid, DxColumn } from "devextreme-vue/data-grid";
import { mapActions, mapState, mapGetters } from "vuex";
import ProductDataFormatter from "@/pages/wizardNutrition/report-formatter/product-data-formatter";

export default {
  name: "ProductResume",
  components: {
    DxDataGrid,
    DxColumn,
    DxChart,
    DxSeries,
    DxCommonSeriesSettings,
    DxLegend,
    DxSeriesTemplate,
    DxTooltip,
    DxSize,
    DxValueAxis,
    DxLabel,
  },
  props: ["fertilization"],
  data() {
    return {
      productResume: [],
      stackProduct: [],
      plantation: {},
    };
  },
  computed: {
    ...mapState("Nutrition", ["productsNotInNutritionPlan"]),
    ...mapGetters("nutritionStepper", ["nutritionIrrigationData"]),
    chartDataWithAxis() {
      // Analizar todos los valores para detectar productos con valores muy pequeños
      if (!this.stackProduct || this.stackProduct.length === 0) {
        return { data: this.stackProduct, useSecondAxis: false };
      }

      // Agrupar valores por producto
      const productValues = {};
      this.stackProduct.forEach(item => {
        if (!productValues[item.productName]) {
          productValues[item.productName] = [];
        }
        productValues[item.productName].push(item.value || 0);
      });

      // Calcular promedio por producto
      const productAverages = {};
      Object.keys(productValues).forEach(product => {
        const values = productValues[product].filter(v => v > 0);
        if (values.length > 0) {
          productAverages[product] = values.reduce((a, b) => a + b, 0) / values.length;
        }
      });

      const allAverages = Object.values(productAverages);
      if (allAverages.length === 0) {
        return { data: this.stackProduct, useSecondAxis: false };
      }

      const maxAvg = Math.max(...allAverages);
      const minAvg = Math.min(...allAverages);

      // Si la diferencia es mayor a 30:1, usar segundo eje para productos pequeños
      const useSecondAxis = (maxAvg / minAvg) > 30;

      if (!useSecondAxis) {
        return { data: this.stackProduct, useSecondAxis: false };
      }

      // Determinar umbral (productos por debajo del 20% del máximo van al segundo eje)
      const threshold = maxAvg * 0.2;
      const smallProducts = Object.keys(productAverages).filter(
        product => productAverages[product] < threshold
      );

      // Asignar eje a cada dato y ordenar: primero grandes (primary), luego pequeños (secondary)
      const dataWithAxis = this.stackProduct.map(item => ({
        ...item,
        axis: smallProducts.includes(item.productName) ? 'secondary' : 'primary'
      }));

      dataWithAxis.sort((a, b) => {
        const orderA = Number.isFinite(a.periodIndex) ? a.periodIndex : Number.MAX_SAFE_INTEGER;
        const orderB = Number.isFinite(b.periodIndex) ? b.periodIndex : Number.MAX_SAFE_INTEGER;

        if (orderA !== orderB) return orderA - orderB;
        return String(a.productName || '').localeCompare(String(b.productName || ''));
      });

      return { data: dataWithAxis, useSecondAxis: true, smallProducts };
    },
  },
  async mounted() {
    this.setProductsNotInNutritionPlan([]);
    const formatter = new ProductDataFormatter(this.fertilization);
    try {
      await formatter.buildProductResumeData();
    } catch (e) {
      const notFoundNutritionProducts = JSON.parse(e.message);
      this.setProductsNotInNutritionPlan(notFoundNutritionProducts);
    }

    this.productResume = formatter.productResume;
    this.stackProduct = formatter.stackProduct;
    this.plantation = formatter.plantation;
  },
  methods: {
    onLegendClick({ target: series }) {
      if (series.isVisible()) {
        series.hide();
      } else {
        series.show();
      }
    },
    customizeTooltip(point) {
      const units =
        this.nutritionIrrigationData.dosis_unit === 1 ? "kg/ha" : "g/pie";
      return {
        html: `<div><div class='tooltip-header'>${
          point.argumentText
        }</div><div class='tooltip-body'>
          <span class='bold'>${this.$t("product")}: ${point.seriesName} </span>
          </div>
          <span class="bold"> ${this.$t("weather_parameter_value")}: ${
          point.value
        } ${units}</span> `,
      };
    },
    customizeSeries(seriesName) {
      // Asignar el eje correcto a cada serie
      if (this.chartDataWithAxis.useSecondAxis && 
          this.chartDataWithAxis.smallProducts?.includes(seriesName)) {
        return { axis: 'secondary' };
      }
      return { axis: 'primary' };
    },
    customizeAxisLabel(arg) {
      return arg.value;
    },
    customizeSecondaryAxisLabel(arg) {
      const numericValue = Number(arg?.value);
      if (Number.isFinite(numericValue) && numericValue !== 0 && Math.abs(numericValue) < 1) {
        return numericValue.toFixed(1);
      }
      return arg?.valueText ?? arg?.value;
    },
    ...mapActions("Nutrition", ["setProductsNotInNutritionPlan"]),
  },
};
</script>
